// src/pages/Admin/ReglementInscription/components/EditReglementInscriptionDrawer.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaCalculator,
  FaEuroSign,
  FaSave,
  FaTimes,
  FaUserCheck,
} from "react-icons/fa";
import { toast } from "react-toastify";

import ReglementDrawer from "./ReglementDrawer";

import { updateReglementInscription } from "../../../../../services/reglementinscriptionServices";
import { getSettings } from "../../../../../services/settingsServices";

import {
  extractArray,
  fmtEUR,
  getAdherentName,
  neighborsFromSeason,
  STATUTS_REGLEMENT,
  TYPES_REGLEMENT,
  toNumber,
} from "../reglement.helpers";

const initialForm = {
  id: "",
  saison: "",
  montant_lic_assurance: "",
  montant_adhesion: "",
  montant_cotisation: "",
  reduction_foyer: "",
  pass_sport: "",
  reduction_saison: "",
  type_reglement: "",
  nb_reglement: "",
  observations: "",
  statut_reglement: "",
};

function FieldLabel({ children, required = false }) {
  return (
    <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
      {children}
      {required ? <span className="ml-1 text-rose-500">*</span> : null}
    </label>
  );
}

function SectionCard({ icon, title, subtitle, children }) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/60">
      <div className="mb-4 flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
          {icon}
        </span>

        <div>
          <h3 className="font-bold text-slate-900 dark:text-white">{title}</h3>
          {subtitle ? (
            <p className="text-sm text-slate-600 dark:text-slate-300">
              {subtitle}
            </p>
          ) : null}
        </div>
      </div>

      {children}
    </div>
  );
}

export default function EditReglementInscriptionDrawer({
  isOpen,
  onClose,
  reglement,
  refreshList,
  syncDossierValidateReglement,
}) {
  const [seasons, setSeasons] = useState([]);
  const [loadingContext, setLoadingContext] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState(initialForm);

  const adherent = reglement?.Adherent || {};
  const adherentName = reglement ? getAdherentName(reglement) : "Adhérent";

  const liveTotal = useMemo(() => {
    return (
      toNumber(form.montant_lic_assurance) +
      toNumber(form.montant_cotisation) +
      toNumber(form.reduction_foyer) +
      toNumber(form.pass_sport) +
      toNumber(form.reduction_saison)
    );
  }, [form]);

  const loadSeasons = useCallback(async () => {
    try {
      setLoadingContext(true);

      const settingsResponse = await getSettings();
      const settingsRows = extractArray(settingsResponse);
      const active = settingsRows?.[0]?.saison_active || "";

      const options = Array.from(
        new Set([form.saison, ...neighborsFromSeason(active)].filter(Boolean))
      );

      setSeasons(options);
    } catch (error) {
      console.error("Erreur chargement saisons :", error);
      toast.error("Impossible de charger les saisons.");
    } finally {
      setLoadingContext(false);
    }
  }, [form.saison]);

  useEffect(() => {
    if (!isOpen || !reglement) return;

    setForm({
      id: reglement?.id || "",
      saison: reglement?.saison || "",
      montant_lic_assurance: reglement?.montant_lic_assurance ?? "",
      montant_adhesion: reglement?.montant_adhesion ?? "",
      montant_cotisation: reglement?.montant_cotisation ?? "",
      reduction_foyer: reglement?.reduction_foyer ?? "",
      pass_sport: reglement?.pass_sport ?? "",
      reduction_saison: reglement?.reduction_saison ?? "",
      type_reglement: reglement?.type_reglement || "",
      nb_reglement: reglement?.nb_reglement ?? "",
      observations: reglement?.observations || "",
      statut_reglement: reglement?.statut_reglement || "",
    });

    setSaving(false);
  }, [isOpen, reglement]);

  useEffect(() => {
    if (!isOpen) return;
    loadSeasons();
  }, [isOpen, loadSeasons]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const canSubmit =
    !saving &&
    !loadingContext &&
    String(form.id || "").trim() !== "" &&
    String(form.saison || "").trim() !== "" &&
    String(form.statut_reglement || "").trim() !== "";

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!canSubmit) return;

    try {
      setSaving(true);

      const payload = {
        id: form.id,
        saison: form.saison,
        montant_lic_assurance:
          form.montant_lic_assurance === "" ? null : toNumber(form.montant_lic_assurance),
        montant_adhesion:
          form.montant_adhesion === "" ? null : toNumber(form.montant_adhesion),
        montant_cotisation:
          form.montant_cotisation === "" ? null : toNumber(form.montant_cotisation),
        reduction_foyer:
          form.reduction_foyer === "" ? null : toNumber(form.reduction_foyer),
        pass_sport: form.pass_sport === "" ? null : toNumber(form.pass_sport),
        reduction_saison:
          form.reduction_saison === "" ? null : toNumber(form.reduction_saison),
        type_reglement: form.type_reglement || "",
        nb_reglement:
          form.nb_reglement === "" || form.nb_reglement === null
            ? null
            : Number(form.nb_reglement),
        observations: form.observations || "",
        statut_reglement: form.statut_reglement,
      };

      await updateReglementInscription(payload);

      const adherentId = reglement?.Adherent?.id || reglement?.adherent_id;

      await syncDossierValidateReglement?.(
        adherentId,
        payload.saison,
        payload.statut_reglement
      );

      toast.success("Règlement mis à jour avec succès.");

      await refreshList?.();
      onClose?.();
    } catch (error) {
      console.error("Erreur mise à jour règlement :", error);
      toast.error("Erreur lors de la mise à jour du règlement.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ReglementDrawer
      isOpen={isOpen}
      onClose={onClose}
      title="Modifier un règlement"
      subtitle={`${adherentName}${form.saison ? ` · Saison ${form.saison}` : ""}`}
      width="max-w-4xl"
    >
      <form onSubmit={handleSubmit} className="flex min-h-full flex-col">
        <div className="flex-1 space-y-5 p-4 sm:p-6">
          <SectionCard
            icon={<FaUserCheck />}
            title="Adhérent & saison"
            subtitle="L'adhérent est lié au règlement. Seule la saison peut être modifiée ici."
          >
            <div className="grid gap-4 lg:grid-cols-2">
              <div>
                <FieldLabel>Adhérent</FieldLabel>
                <div className="rounded-2xl border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-950">
                  <p className="text-sm font-bold text-slate-900 dark:text-white">
                    {adherentName}
                  </p>
                  {/* <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                    {adherent?.email || "Email non renseigné"} · ID{" "}
                    {adherent?.id || "—"}
                  </p> */}
                </div>
              </div>

              <div>
                <FieldLabel required>Saison</FieldLabel>
                <select
                  name="saison"
                  value={form.saison}
                  onChange={handleChange}
                  disabled={loadingContext}
                  required
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                >
                  <option value="">
                    {loadingContext ? "Chargement..." : "— Sélectionner —"}
                  </option>

                  {seasons.map((season) => (
                    <option key={season} value={season}>
                      {season}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </SectionCard>

          <SectionCard
            icon={<FaEuroSign />}
            title="Montants"
            subtitle="Modifiez les montants du règlement d'inscription."
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <FieldLabel>Licence & assurance</FieldLabel>
                <input
                  type="number"
                  step="0.01"
                  name="montant_lic_assurance"
                  value={form.montant_lic_assurance}
                  onChange={handleChange}
                  placeholder="0.00"
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <div>
                <FieldLabel>Montant Adhésion</FieldLabel>
                <input
                  type="number"
                  step="0.01"
                  name="montant_adhesion"
                  value={form.montant_adhesion}
                  onChange={handleChange}
                  placeholder="0.00"
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <div>
                <FieldLabel>Cotisation</FieldLabel>
                <input
                  type="number"
                  step="0.01"
                  name="montant_cotisation"
                  value={form.montant_cotisation}
                  onChange={handleChange}
                  placeholder="0.00"
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <div>
                <FieldLabel>Réduction foyer</FieldLabel>
                <input
                  type="number"
                  step="0.01"
                  name="reduction_foyer"
                  value={form.reduction_foyer}
                  onChange={handleChange}
                  placeholder="-20.00"
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <div>
                <FieldLabel>Pass'Sport</FieldLabel>
                <input
                  type="number"
                  step="0.01"
                  name="pass_sport"
                  value={form.pass_sport}
                  onChange={handleChange}
                  placeholder="-70.00"
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <div>
                <FieldLabel>Réduction saison</FieldLabel>
                <input
                  type="number"
                  step="0.01"
                  name="reduction_saison"
                  value={form.reduction_saison}
                  onChange={handleChange}
                  placeholder="-10.00"
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <div className="flex items-end">
                <div className="w-full rounded-2xl border border-indigo-200 bg-indigo-50 px-4 py-3 dark:border-indigo-500/20 dark:bg-indigo-500/10">
                  <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-indigo-700 dark:text-indigo-300">
                    Total provisoire
                  </p>
                  <p className="mt-1 text-2xl font-extrabold text-slate-950 dark:text-white">
                    {fmtEUR(liveTotal)}
                  </p>
                </div>
              </div>
            </div>
          </SectionCard>

          <SectionCard
            icon={<FaCalculator />}
            title="Mode & statut"
            subtitle="Le statut synchronise la validation du règlement dans le dossier."
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <FieldLabel>Type de règlement</FieldLabel>
                <select
                  name="type_reglement"
                  value={form.type_reglement}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                >
                  <option value="">— Sélectionner —</option>
                  {TYPES_REGLEMENT.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <FieldLabel>Nombre de règlements</FieldLabel>
                <input
                  type="number"
                  min="1"
                  name="nb_reglement"
                  value={form.nb_reglement}
                  onChange={handleChange}
                  placeholder="1"
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <div>
                <FieldLabel required>Statut</FieldLabel>
                <select
                  name="statut_reglement"
                  value={form.statut_reglement}
                  onChange={handleChange}
                  required
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                >
                  <option value="">— Sélectionner —</option>
                  {STATUTS_REGLEMENT.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sm:col-span-2">
                <FieldLabel>Observations</FieldLabel>
                <textarea
                  name="observations"
                  value={form.observations}
                  onChange={handleChange}
                  rows={4}
                  placeholder="N° Pass'Sport, détails du règlement, remarques..."
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>
            </div>
          </SectionCard>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 p-4 dark:border-slate-800 sm:px-6">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaTimes />
            Annuler
          </button>

          <button
            type="submit"
            disabled={!canSubmit}
            className="inline-flex items-center gap-2 rounded-2xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FaSave />
            {saving ? "Enregistrement..." : "Mettre à jour"}
          </button>
        </div>
      </form>
    </ReglementDrawer>
  );
}